// Lib
import { FC, useEffect, useState } from "react";
// Api
import {
  useLazyGetCoreSettingsQuery,
  useUpdateCoreSettingsMutation,
} from "rtkQuery/query/coreSettingsAPI";
// Hooks
import { useNotification, usePermissions } from "hooks";
// Helpers
import { getConfirmModalTitle } from "../../helpers";
// Util
import { errorHandler } from "utils/errorHandler";
// Icons
import { PersonIcon } from "icons";
// Components
import { ConfirmDialog } from "components/Modals";
import SettingsItem from "../SettingsItem";
import { Input, Select } from "../Inputs";
// Styled
import { ContentBox } from "styled/Box";
import { Typography } from "styled/Typography";

import {
  AIField,
  AISettingsKey,
  ConfirmModalType,
  initialLoadingState,
  initialState,
  LoadingStateValues,
  notificationMessage,
  StateValues,
} from "./config";
import { Switch } from "../Inputs/Switch";
import { getSettingsName } from "pages/Settings/helpers";
import { AiModeEnum } from "types/coreSettings";
import { OptionsSelect } from "../Inputs/OptionsSelect";

export const AiSettings: FC = () => {
  const { openNotification } = useNotification();

  const { canSettingsGet, canSettingsUpdate } = usePermissions();

  const [fetchCoreSettings] = useLazyGetCoreSettingsQuery();
  const [updateCoreSettings] = useUpdateCoreSettingsMutation();

  const [confirmModal, setConfirmModal] = useState<ConfirmModalType | false>(
    false,
  );

  const [settings, setSettings] = useState<StateValues>(initialState);

  const [isLoading, setIsLoading] =
    useState<LoadingStateValues>(initialLoadingState);

  const getCoreSettings = async () => {
    try {
      setIsLoading(prev => ({
        ...prev,
        [AIField.IsAIAssistantAllowed]: true,
        [AIField.IsAIAssistantForUsersAllowed]: true,
      }));

      const { isAIAssistantAllowed, isAIAssistantForUsersAllowed, aiMode } =
        await fetchCoreSettings().unwrap();

      setSettings(prev => ({
        ...prev,
        isAIAssistantAllowed,
        isAIAssistantForUsersAllowed,
        aiMode,
      }));
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({
        ...prev,
        [AIField.IsAIAssistantAllowed]: false,
        [AIField.IsAIAssistantForUsersAllowed]: false,
      }));
    }
  };

  useEffect(() => {
    if (!canSettingsGet) {
      return;
    }

    getCoreSettings();
  }, []);

  const handleUpdateCoreBooleanSettings = async (
    key: AIField,
    value: boolean,
  ) => {
    try {
      setIsLoading(prev => ({ ...prev, [key]: true }));

      const payload = {
        [key]: value,
      };

      setSettings(prev => ({ ...prev, [key]: value }));

      await updateCoreSettings(payload).unwrap();

      openNotification({ message: notificationMessage[key] });
    } catch (error) {
      setSettings(prev => ({ ...prev, [key]: !value }));

      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));

      if (confirmModal) {
        setConfirmModal(false);
      }
    }
  };

  const handleUpdateCoreSettings = async (
    key: AISettingsKey,
    value: number,
  ) => {
    try {
      setIsLoading(prev => ({ ...prev, [key]: true }));

      await updateCoreSettings({ [key]: value }).unwrap();

      setSettings(prev => ({ ...prev, [key]: value }));

      openNotification({ message: notificationMessage[key] });
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleUpdateBooleanUpdate = (key: AIField, value: boolean) => {
    setSettings(prev => ({ ...prev, [key]: value as boolean }));
    setConfirmModal({ key, value });
  };

  const onConfirm = () => {
    if (!confirmModal) return;

    if (
      confirmModal.key === AIField.IsAIAssistantAllowed ||
      confirmModal.key === AIField.IsAIAssistantForUsersAllowed
    ) {
      handleUpdateCoreBooleanSettings(confirmModal.key, confirmModal.value);

      return;
    }
  };

  const onCancel = () => {
    if (!confirmModal) return;
    setSettings(prev => ({ ...prev, [confirmModal.key]: !confirmModal.value }));
    setConfirmModal(false);
  };

  return (
    <div>
      <ContentBox $column $gap={12}>
        <Typography.H2>AI Settings</Typography.H2>

        <div>
          <SettingsItem title={getSettingsName(AIField.IsAIAssistantAllowed)}>
            <Switch
              isLoading={isLoading[AIField.IsAIAssistantAllowed]}
              canUpdate={canSettingsUpdate && canSettingsUpdate}
              data={settings[AIField.IsAIAssistantAllowed]}
              onChange={value =>
                handleUpdateBooleanUpdate(AIField.IsAIAssistantAllowed, value)
              }
            />
          </SettingsItem>

          <SettingsItem
            title={getSettingsName(AIField.IsAIAssistantForUsersAllowed)}
          >
            <Switch
              isLoading={isLoading[AIField.IsAIAssistantForUsersAllowed]}
              canUpdate={canSettingsUpdate && canSettingsUpdate}
              data={settings[AIField.IsAIAssistantForUsersAllowed]}
              onChange={value =>
                handleUpdateBooleanUpdate(
                  AIField.IsAIAssistantForUsersAllowed,
                  value,
                )
              }
            />
          </SettingsItem>

          <SettingsItem title={getSettingsName(AIField.AiMode)}>
            <OptionsSelect
              width={230}
              isLoading={isLoading[AIField.AiMode]}
              canUpdate={
                canSettingsUpdate &&
                canSettingsUpdate &&
                !!settings[AIField.IsAIAssistantAllowed]
              }
              data={settings[AIField.AiMode]}
              onChange={function (value): void {
                handleUpdateCoreSettings(AIField.AiMode, value);
              }}
              options={[
                { label: "Only selected customers", value: null },
                { label: "Loyalty feature", value: AiModeEnum.LOYALTY },
                { label: "Packages feature", value: AiModeEnum.PACKAGES },
              ]}
            />
          </SettingsItem>
        </div>
      </ContentBox>

      <ConfirmDialog
        isLoading={confirmModal && isLoading[confirmModal.key]}
        open={!!confirmModal}
        Icon={PersonIcon}
        message={
          confirmModal &&
          getConfirmModalTitle({
            key: confirmModal.key,
            value: confirmModal.value,
          })
        }
        description="Are you sure to continue this action?"
        onCancel={onCancel}
        firstCTAButton={{
          title: "Accept",
          status: "danger",
          disabled: false,
          loading: confirmModal && isLoading[confirmModal.key],
          onClick: onConfirm,
        }}
      />
    </div>
  );
};
